import React, { useEffect, useRef, useState } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import { VscDebugRestart } from "react-icons/vsc";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { FiChevronDown, FiCrop, FiGrid, FiRepeat } from "react-icons/fi";
import "./PreviewPlaybackBar.css";
import { commonFps } from "../Render/render.constants";
import { speedDropdownOptions } from "../Render/scrollSpeedMap";

const PreviewPlaybackBar = ({
  isPlaying,
  onPlayPause,
  onRestart,
  isLooping,
  onLoopToggle,
  progress,
  currentLabel,
  durationLabel,
  onSeek,
  onSeekStart,
  onSeekEnd,
  speed,
  onSpeedChange,
  fps,
  onFpsChange,
  isFullscreen,
  onFullscreenToggle,
  isTitleSafe,
  onTitleSafeToggle,
  isGuidesVisible,
  onGuidesToggle,
  isVisible = true,
  onBarMouseEnter,
  onBarMouseLeave,
}) => {
  const [speedOpen, setSpeedOpen] = useState(false);
  const [fpsOpen, setFpsOpen] = useState(false);
  const speedRef = useRef(null);
  const fpsRef = useRef(null);
  const progressPct = Math.max(0, Math.min(100, (progress || 0) * 100));

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (speedRef.current && !speedRef.current.contains(e.target)) {
        setSpeedOpen(false);
      }
      if (fpsRef.current && !fpsRef.current.contains(e.target)) {
        setFpsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setSpeedOpen(false);
      setFpsOpen(false);
    }
  }, [isVisible]);

  // const barClass =
  //   "ppb-bar" +
  //   (isVisible ? "" : " is-hidden") +
  //   (isCompact ? " is-compact" : "");
  const barClass =
    "ppb-bar" +
    (isVisible ? "" : " is-hidden") +
    (isFullscreen ? " is-fullscreen" : "");

  return (
    <div
      className={barClass}
      data-html2canvas-ignore="true"
      aria-hidden={!isVisible}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onBarMouseEnter}
      onMouseLeave={onBarMouseLeave}
    >
      <div className="ppb-leading">
        <button
          type="button"
          className="ppb-btn ppb-btn-play"
          onClick={onPlayPause}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FaPause size={11} /> : <FaPlay size={11} />}
          <span>{isPlaying ? "Pause" : "Play"}</span>
        </button>

        {/* <span className="ppb-time ppb-time-compact">
          {currentLabel} / {durationLabel}
        </span> */}
      </div>

      <div className="ppb-extras">
        <div
          className="ppb-quick-controls"
          role="group"
          aria-label="Playback options"
        >
          <button
            type="button"
            className="ppb-icon-action"
            onClick={onRestart}
            aria-label="Restart"
            data-tooltip="Restart"
          >
            <VscDebugRestart size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`ppb-icon-action ${isLooping ? "is-active" : ""}`}
            onClick={onLoopToggle}
            aria-label="Loop playback"
            aria-pressed={Boolean(isLooping)}
            data-tooltip="Loop"
          >
            <FiRepeat size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="ppb-seek-wrap">
          <input
            type="range"
            className="ppb-seek"
            min={0}
            max={1}
            step={0.001}
            value={Number.isFinite(progress) ? progress : 0}
            style={{ "--ppb-progress": `${progressPct}%` }}
            onMouseDown={onSeekStart}
            onTouchStart={onSeekStart}
            onChange={(e) => onSeek(Number(e.target.value))}
            onMouseUp={onSeekEnd}
            onTouchEnd={onSeekEnd}
            aria-label="Seek"
          />
        </div>

        <span
          className="ppb-time"
          aria-label={`Runtime ${currentLabel} of ${durationLabel}`}
        >
          <span className="ppb-time-value">
            {currentLabel} / {durationLabel}
          </span>
        </span>

        <div className="ppb-divider" aria-hidden="true" />

        <div className="ppb-speed" ref={speedRef}>
          <button
            type="button"
            className="ppb-speed-trigger"
            onClick={() => {
              setFpsOpen(false);
              setSpeedOpen((o) => !o);
            }}
            aria-expanded={speedOpen}
            aria-haspopup="listbox"
            aria-label={`Speed ${speed}x`}
          >
            <span className="ppb-speed-label">Speed</span>
            <span className="ppb-speed-value">{speed}x</span>
            <FiChevronDown size={14} />
          </button>
          {speedOpen && (
            <div className="ppb-speed-menu" role="listbox">
              {speedDropdownOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === speed}
                  className={
                    "ppb-speed-option" +
                    (option.value === speed ? " is-active" : "")
                  }
                  onClick={() => {
                    onSpeedChange(option.value);
                    setSpeedOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ppb-divider" aria-hidden="true" />

        <div className="ppb-speed ppb-fps" ref={fpsRef}>
          <button
            type="button"
            className="ppb-speed-trigger"
            onClick={() => {
              setSpeedOpen(false);
              setFpsOpen((o) => !o);
            }}
            aria-expanded={fpsOpen}
            aria-haspopup="listbox"
            aria-label={`FPS ${fps}`}
          >
            <span className="ppb-speed-label">FPS</span>
            <span className="ppb-speed-value">{fps}</span>
            <FiChevronDown size={14} />
          </button>
          {fpsOpen && (
            <div className="ppb-speed-menu ppb-fps-menu" role="listbox">
              {commonFps.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={option === fps}
                  className={
                    "ppb-speed-option" +
                    (option === fps ? " is-active" : "")
                  }
                  onClick={() => {
                    onFpsChange(option);
                    setFpsOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>

        {isFullscreen && (
          <>
            <div className="ppb-divider" aria-hidden="true" />
            <div
              className="ppb-overlay-controls"
              role="group"
              aria-label="Preview overlays"
            >
              <button
                type="button"
                className={`ppb-overlay-toggle ${isTitleSafe ? "is-active" : ""}`}
                aria-label={`${isTitleSafe ? "Hide" : "Show"} title safe`}
                aria-pressed={Boolean(isTitleSafe)}
                data-tooltip={`${isTitleSafe ? "Hide" : "Show"} Title Safe`}
                onClick={onTitleSafeToggle}
              >
                <FiCrop size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`ppb-overlay-toggle ${isGuidesVisible ? "is-active" : ""}`}
                aria-label={`${isGuidesVisible ? "Hide" : "Show"} guides`}
                aria-pressed={Boolean(isGuidesVisible)}
                data-tooltip={`${isGuidesVisible ? "Hide" : "Show"} Guides`}
                onClick={onGuidesToggle}
              >
                <FiGrid size={16} aria-hidden="true" />
              </button>
            </div>
          </>
        )}

        <div className="ppb-divider" aria-hidden="true" />

        <button
          type="button"
          className="ppb-btn"
          onClick={onFullscreenToggle}
          aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? (
            <MdFullscreenExit size={18} />
          ) : (
            <MdFullscreen size={18} />
          )}
          <span>{isFullscreen ? "Exit" : "Fullscreen"}</span>
        </button>
      </div>
    </div>
  );
};

export default PreviewPlaybackBar;
